import dayjs from 'dayjs';
import {
  ChecklistGroup,
  groupDocsIntoChecklistItems,
} from '~/common/pages/Compliance/utils/buildChecklistItems.utils';
import { ComplianceDocumentSummary } from '../components/types';

/**
 * A platform checklist item plus every ingestion
 * document that belongs to it. The agent acts at this level — ignore-and-resubmit
 * marks the whole activity resubmitted — so the redesigned page selects a
 * checklist item and shows its documents as tabs.
 *
 * Documents without a supported checklist identity each become their own
 * single-document item.
 *
 * The grouping itself comes from the shared {@link groupDocsIntoChecklistItems}
 * (so the review page, the document list, and the embedded drawer agree on
 * "which docs are the same checklist item"); this layer adds the agent-review
 * state (`needsReview`, `resubmittedAt`) on top.
 */
export interface ChecklistItem
  extends ChecklistGroup<ComplianceDocumentSummary> {
  /**
   * True when ANY document under this item needs the agent's review (rejected,
   * not yet resubmitted). Drives the "Needs review" vs "Other documents" split.
   */
  needsReview: boolean;
  /**
   * The most recent `agentResubmittedAt` among the item's documents, or null if
   * none have been resubmitted. Gates the "Resubmitted" disabled action.
   */
  resubmittedAt: string | null;
}

export interface ChecklistItems {
  /** Every checklist item, in first-appearance (most-recent-first) order. */
  items: ChecklistItem[];
  /** Items that need the agent's review — the left rail "Needs review" list. */
  needsReviewItems: ChecklistItem[];
  /** The remaining items — the collapsible "Other documents" list. */
  otherItems: ChecklistItem[];
}

const maxDate = (
  a: string | null,
  b: string | null | undefined
): string | null => {
  if (b == null) {
    return a;
  }
  if (a == null) {
    return b;
  }

  return dayjs(b).isAfter(dayjs(a)) ? b : a;
};

/**
 * Groups the overview document summaries into platform checklist items, then
 * splits them into the needs-review and other buckets the rail renders.
 *
 * Order follows the incoming `documentUpdatedAt` desc order: each item is
 * positioned at its first (most-recent) member, and documents stay in their
 * incoming order within an item.
 */
export const buildChecklistItems = (
  documents: ComplianceDocumentSummary[]
): ChecklistItems => {
  // Group with the shared grouper, then fold in the agent-review state.
  const items: ChecklistItem[] = groupDocsIntoChecklistItems(
    documents,
    (document) => ({
      id: document.ingestionDocumentId,
      documentName: document.documentName,
      skyslopeActivityId: document.skyslopeActivityId,
      skyslopeActivityName: document.skyslopeActivityName,
      kwComplianceItemId: document.kwComplianceItemId,
      kwComplianceDocument: document.kwComplianceDocument,
    })
  ).map((group) => {
    let needsReview = false;
    let resubmittedAt: string | null = null;
    for (const document of group.docs) {
      needsReview = needsReview || document.needsReviewByAgent;
      resubmittedAt = maxDate(resubmittedAt, document.agentResubmittedAt);
    }

    return { ...group, needsReview, resubmittedAt };
  });

  const otherItems = items.filter((item) => !item.needsReview);

  return {
    items,
    needsReviewItems: items.filter((item) => item.needsReview),
    // Real checklist items first; standalone "not on checklist" documents sink
    // to the bottom (stable within each group).
    otherItems: [
      ...otherItems.filter((item) => item.isChecklistItem),
      ...otherItems.filter((item) => !item.isChecklistItem),
    ],
  };
};

/** Finds the checklist item whose documents contain the given document id. */
export const findItemForDocument = (
  items: ChecklistItem[],
  ingestionDocumentId: string | undefined
): ChecklistItem | undefined => {
  if (ingestionDocumentId == null) {
    return undefined;
  }

  return items.find((item) =>
    item.docs.some((doc) => doc.ingestionDocumentId === ingestionDocumentId)
  );
};
